import { Tools } from './tools';
import { Texture } from '../Materials/Textures/texture';
import { InternalTexture, InternalTextureSource } from '../Materials/Textures/internalTexture';
import { Scalar } from '../Maths/math.scalar';
/**
 * Info about the .basis files
 */
var BasisFileInfo = /** @class */ (function () {
    function BasisFileInfo() {
    }
    return BasisFileInfo;
}());
/**
 * Result of transcoding a basis file
 */
var TranscodeResult = /** @class */ (function () {
    function TranscodeResult() {
    }
    return TranscodeResult;
}());
/**
 * Configuration options for the Basis transcoder
 */
var BasisTranscodeConfiguration = /** @class */ (function () {
    function BasisTranscodeConfiguration() {
    }
    return BasisTranscodeConfiguration;
}());
export { BasisTranscodeConfiguration };
/**
 * @hidden
 * Enum of basis transcoder formats
 */
var BASIS_FORMATS;
(function (BASIS_FORMATS) {
    BASIS_FORMATS[BASIS_FORMATS["cTFETC1"] = 0] = "cTFETC1";
    BASIS_FORMATS[BASIS_FORMATS["cTFBC1"] = 1] = "cTFBC1";
    BASIS_FORMATS[BASIS_FORMATS["cTFBC4"] = 2] = "cTFBC4";
    BASIS_FORMATS[BASIS_FORMATS["cTFPVRTC1_4_OPAQUE_ONLY"] = 3] = "cTFPVRTC1_4_OPAQUE_ONLY";
    BASIS_FORMATS[BASIS_FORMATS["cTFBC7_M6_OPAQUE_ONLY"] = 4] = "cTFBC7_M6_OPAQUE_ONLY";
    BASIS_FORMATS[BASIS_FORMATS["cTFETC2"] = 5] = "cTFETC2";
    BASIS_FORMATS[BASIS_FORMATS["cTFBC3"] = 6] = "cTFBC3";
    BASIS_FORMATS[BASIS_FORMATS["cTFBC5"] = 7] = "cTFBC5";
})(BASIS_FORMATS || (BASIS_FORMATS = {}));
/**
 * Used to load .Basis files
 * See https://github.com/BinomialLLC/basis_universal/tree/master/webgl
 */
var BasisTools = /** @class */ (function () {
    function BasisTools() {
    }
    /**
     * Get the internal format to be passed to texImage2D corresponding to the .basis format value
     * @param basisFormat format chosen from GetSupportedTranscodeFormat
     * @returns internal format corresponding to the Basis format
     */
    BasisTools.GetInternalFormatFromBasisFormat = function (basisFormat) {
        // Corrisponding internal formats
        var COMPRESSED_RGB_S3TC_DXT1_EXT = 0x83F0;
        var COMPRESSED_RGBA_S3TC_DXT5_EXT = 0x83F3;
        var RGB_ETC1_Format = 36196;
        if (basisFormat === BASIS_FORMATS.cTFETC1) {
            return RGB_ETC1_Format;
        }
        else if (basisFormat === BASIS_FORMATS.cTFBC1) {
            return COMPRESSED_RGB_S3TC_DXT1_EXT;
        }
        else if (basisFormat === BASIS_FORMATS.cTFBC3) {
            return COMPRESSED_RGBA_S3TC_DXT5_EXT;
        }
        else {
            throw "The chosen Basis transcoder format is not currently supported";
        }
    };
    BasisTools._CreateWorkerAsync = function () {
        var _this = this;
        if (!this._WorkerPromise) {
            this._WorkerPromise = new Promise(function (res) {
                if (_this._Worker) {
                    res(_this._Worker);
                }
                else {
                    Tools.LoadFileAsync(BasisTools.WasmModuleURL).then(function (wasmBinary) {
                        var workerBlobUrl = URL.createObjectURL(new Blob(["(" + workerFunc + ")()"], { type: "application/javascript" }));
                        _this._Worker = new Worker(workerBlobUrl);
                        var initHandler = function (msg) {
                            if (msg.data.action === "init") {
                                _this._Worker.removeEventListener("message", initHandler);
                                res(_this._Worker);
                            }
                        };
                        _this._Worker.addEventListener("message", initHandler);
                        _this._Worker.postMessage({ action: "init", url: BasisTools.JSModuleURL, wasmBinary: wasmBinary });
                    });
                }
            });
        }
        return this._WorkerPromise;
    };
    /**
     * Transcodes a loaded image file to compressed pixel data
     * @param data image data to transcode
     * @param config configuration options for the transcoding
     * @returns a promise resulting in the transcoded image
     */
    BasisTools.TranscodeAsync = function (data, config) {
        var _this = this;
        var dataView = data instanceof ArrayBuffer ? new Uint8Array(data) : data;
        return new Promise(function (res, rej) {
            _this._CreateWorkerAsync().then(function () {
                var actionId = _this._actionId++;
                var messageHandler = function (msg) {
                    if (msg.data.action === "transcode" && msg.data.id === actionId) {
                        _this._Worker.removeEventListener("message", messageHandler);
                        if (!msg.data.success) {
                            rej("Transcode is not supported on this device");
                        }
                        else {
                            res(msg.data);
                        }
                    }
                };
                _this._Worker.addEventListener("message", messageHandler);
                var dataViewCopy = new Uint8Array(dataView.byteLength);
                dataViewCopy.set(new Uint8Array(dataView.buffer, dataView.byteOffset, dataView.byteLength));
                _this._Worker.postMessage({ action: "transcode", id: actionId, imageData: dataViewCopy, config: config, ignoreSupportedFormats: _this._IgnoreSupportedFormats }, [dataViewCopy.buffer]);
            });
        });
    };
    /**
     * Loads a texture from the transcode result
     * @param texture texture load to
     * @param transcodeResult the result of transcoding the basis file to load from
     */
    BasisTools.LoadTextureFromTranscodeResult = function (texture, transcodeResult) {
        var engine = texture.getEngine();
        var _loop_1 = function () {
            rootImage = transcodeResult.fileInfo.images[i].levels[0];
            texture._invertVScale = texture.invertY;
            if (transcodeResult.format === -1) {
                // No compatable compressed format found, fallback to RGB
                texture.type = 10;
                texture.format = 4;
                if (engine.webGLVersion < 2 && (Scalar.Log2(rootImage.width) % 1 !== 0 || Scalar.Log2(rootImage.height) % 1 !== 0)) {
                    // Create non power of two texture
                    var source_1 = new InternalTexture(engine, InternalTextureSource.Temp);
                    texture._invertVScale = texture.invertY;
                    source_1.type = 10;
                    source_1.format = 4;
                    // Fallback requires aligned width/height
                    source_1.width = (rootImage.width + 3) & ~3;
                    source_1.height = (rootImage.height + 3) & ~3;
                    engine._bindTextureDirectly(engine._gl.TEXTURE_2D, source_1, true);
                    engine._uploadDataToTextureDirectly(source_1, rootImage.transcodedPixels, i, 0, 4, true);
                    // Resize to power of two
                    engine._rescaleTexture(source_1, texture, engine.scenes[0], engine._getInternalFormat(4), function () {
                        engine._releaseTexture(source_1);
                        engine._bindTextureDirectly(engine._gl.TEXTURE_2D, texture, true);
                    });
                }
                else {
                    // Fallback is already inverted
                    texture._invertVScale = !texture.invertY;
                    // Upload directly
                    texture.width = (rootImage.width + 3) & ~3;
                    texture.height = (rootImage.height + 3) & ~3;
                    engine._uploadDataToTextureDirectly(texture, rootImage.transcodedPixels, i, 0, 4, true);
                }
            }
            else {
                texture.width = rootImage.width;
                texture.height = rootImage.height;
                // Upload all mip levels in the file
                transcodeResult.fileInfo.images[i].levels.forEach(function (level, index) {
                    engine._uploadCompressedDataToTextureDirectly(texture, BasisTools.GetInternalFormatFromBasisFormat(transcodeResult.format), level.width, level.height, level.transcodedPixels, i, index);
                });
                if (engine.webGLVersion < 2 && (Scalar.Log2(texture.width) % 1 !== 0 || Scalar.Log2(texture.height) % 1 !== 0)) {
                    Tools.Warn("Loaded .basis texture width and height are not a power of two. Texture wrapping will be set to Texture.CLAMP_ADDRESSMODE as other modes are not supported with non power of two dimensions in webGL 1.");
                    texture._cachedWrapU = Texture.CLAMP_ADDRESSMODE;
                    texture._cachedWrapV = Texture.CLAMP_ADDRESSMODE;
                }
            }
        };
        var rootImage;
        for (var i = 0; i < transcodeResult.fileInfo.images.length; i++) {
            _loop_1();
        }
    };
    BasisTools._IgnoreSupportedFormats = false;
    /**
     * URL to use when loading the basis transcoder
     */
    BasisTools.JSModuleURL = "https://preview.babylonjs.com/basisTranscoder/basis_transcoder.js";
    /**
     * URL to use when loading the wasm module for the transcoder
     */
    BasisTools.WasmModuleURL = "https://preview.babylonjs.com/basisTranscoder/basis_transcoder.wasm";
    BasisTools._WorkerPromise = null;
    BasisTools._Worker = null;
    BasisTools._actionId = 0;
    return BasisTools;
}());
export { BasisTools };
function workerFunc() {
    var _BASIS_FORMAT = {
        cTFETC1: 0,
        cTFBC1: 1,
        cTFBC4: 2,
        cTFPVRTC1_4_OPAQUE_ONLY: 3,
        cTFBC7_M6_OPAQUE_ONLY: 4,
        cTFETC2: 5,
        cTFBC3: 6,
        cTFBC5: 7,
    };
    var transcoderModulePromise = null;
    onmessage = function (event) {
        if (event.data.action === "init") {
            // Load the transcoder if it hasn't been yet
            if (!transcoderModulePromise) {
                // Override wasm binary
                Module = { wasmBinary: (event.data.wasmBinary) };
                importScripts(event.data.url);
                transcoderModulePromise = new Promise(function (res) {
                    Module.onRuntimeInitialized = function () {
                        Module.initializeBasis();
                        res();
                    };
                });
            }
            transcoderModulePromise.then(function () {
                postMessage({ action: "init" });
            });
        }
        else if (event.data.action === "transcode") {
            // Transcode the basis image and return the resulting pixels
            var config = event.data.config;
            var imgData = event.data.imageData;
            var loadedFile = new Module.BasisFile(imgData);
            var fileInfo = GetFileInfo(loadedFile);
            var format = event.data.ignoreSupportedFormats ? null : GetSupportedTranscodeFormat(event.data.config, fileInfo);
            var needsConversion = false;
            if (format === null) {
                needsConversion = true;
                format = fileInfo.hasAlpha ? _BASIS_FORMAT.cTFBC3 : _BASIS_FORMAT.cTFBC1;
            }
            // Begin transcode
            var success = true;
            if (!loadedFile.startTranscoding()) {
                success = false;
            }
            var buffers = [];
            for (var imageIndex = 0; imageIndex < fileInfo.images.length; imageIndex++) {
                if (!success) {
                    break;
                }
                var image = fileInfo.images[imageIndex];
                if (config.loadSingleImage === undefined || config.loadSingleImage === imageIndex) {
                    var mipCount = image.levels.length;
                    if (config.loadMipmapLevels === false) {
                        mipCount = 1;
                    }
                    for (var levelIndex = 0; levelIndex < mipCount; levelIndex++) {
                        var levelInfo = image.levels[levelIndex];
                        var pixels = TranscodeLevel(loadedFile, imageIndex, levelIndex, format, needsConversion);
                        if (!pixels) {
                            success = false;
                            break;
                        }
                        levelInfo.transcodedPixels = pixels;
                        buffers.push(levelInfo.transcodedPixels.buffer);
                    }
                }
            }
            // Close file
            loadedFile.close();
            loadedFile.delete();
            if (needsConversion) {
                format = -1;
            }
            if (!success) {
                postMessage({ action: "transcode", success: success, id: event.data.id });
            }
            else {
                postMessage({ action: "transcode", success: success, id: event.data.id, fileInfo: fileInfo, format: format }, buffers);
            }
        }
    };
    /**
     * Detects the supported transcode format for the file
     * @param config transcode config
     * @param fileInfo info about the file
     * @returns the chosed format or null if none are supported
     */
    function GetSupportedTranscodeFormat(config, fileInfo) {
        var format = null;
        if (config.supportedCompressionFormats) {
            if (config.supportedCompressionFormats.etc1) {
                format = _BASIS_FORMAT.cTFETC1;
            }
            else if (config.supportedCompressionFormats.s3tc) {
                format = fileInfo.hasAlpha ? _BASIS_FORMAT.cTFBC3 : _BASIS_FORMAT.cTFBC1;
            }
            else if (config.supportedCompressionFormats.pvrtc) {
                // TODO uncomment this after pvrtc bug is fixed is basis transcoder
                // See discussion here: https://github.com/mrdoob/three.js/issues/16524#issuecomment-498929924
                // format = _BASIS_FORMAT.cTFPVRTC1_4_OPAQUE_ONLY;
            }
            else if (config.supportedCompressionFormats.etc2) {
                format = _BASIS_FORMAT.cTFETC2;
            }
        }
        return format;
    }
    /**
     * Retreives information about the basis file eg. dimensions
     * @param basisFile the basis file to get the info from
     * @returns information about the basis file
     */
    function GetFileInfo(basisFile) {
        var hasAlpha = basisFile.getHasAlpha();
        var imageCount = basisFile.getNumImages();
        var images = [];
        for (var i = 0; i < imageCount; i++) {
            var imageInfo = {
                levels: []
            };
            var levelCount = basisFile.getNumLevels(i);
            for (var level = 0; level < levelCount; level++) {
                var levelInfo = {
                    width: basisFile.getImageWidth(i, level),
                    height: basisFile.getImageHeight(i, level)
                };
                imageInfo.levels.push(levelInfo);
            }
            images.push(imageInfo);
        }
        var info = { hasAlpha: hasAlpha, images: images };
        return info;
    }
    function TranscodeLevel(loadedFile, imageIndex, levelIndex, format, convertToRgb565) {
        var dstSize = loadedFile.getImageTranscodedSizeInBytes(imageIndex, levelIndex, format);
        var dst = new Uint8Array(dstSize);
        if (!loadedFile.transcodeImage(dst, imageIndex, levelIndex, format, 1, 0)) {
            return null;
        }
        // If no supported format is found, load as dxt and convert to rgb565
        if (convertToRgb565) {
            var alignedWidth = (loadedFile.getImageWidth(imageIndex, levelIndex) + 3) & ~3;
            var alignedHeight = (loadedFile.getImageHeight(imageIndex, levelIndex) + 3) & ~3;
            dst = ConvertDxtToRgb565(dst, 0, alignedWidth, alignedHeight);
        }
        return dst;
    }
    /**
     * From https://github.com/BinomialLLC/basis_universal/blob/master/webgl/texture/dxt-to-rgb565.js
     * An unoptimized version of dxtToRgb565.  Also, the floating
     * point math used to compute the colors actually results in
     * slightly different colors compared to hardware DXT decoders.
     * @param src dxt src pixels
     * @param srcByteOffset offset for the start of src
     * @param  width aligned width of the image
     * @param  height aligned height of the image
     * @return the converted pixels
     */
    function ConvertDxtToRgb565(src, srcByteOffset, width, height) {
        var c = new Uint16Array(4);
        var dst = new Uint16Array(width * height);
        var blockWidth = width / 4;
        var blockHeight = height / 4;
        for (var blockY = 0; blockY < blockHeight; blockY++) {
            for (var blockX = 0; blockX < blockWidth; blockX++) {
                var i = srcByteOffset + 8 * (blockY * blockWidth + blockX);
                c[0] = src[i] | (src[i + 1] << 8);
                c[1] = src[i + 2] | (src[i + 3] << 8);
                c[2] = (2 * (c[0] & 0x1f) + 1 * (c[1] & 0x1f)) / 3
                    | (((2 * (c[0] & 0x7e0) + 1 * (c[1] & 0x7e0)) / 3) & 0x7e0)
                    | (((2 * (c[0] & 0xf800) + 1 * (c[1] & 0xf800)) / 3) & 0xf800);
                c[3] = (2 * (c[1] & 0x1f) + 1 * (c[0] & 0x1f)) / 3
                    | (((2 * (c[1] & 0x7e0) + 1 * (c[0] & 0x7e0)) / 3) & 0x7e0)
                    | (((2 * (c[1] & 0xf800) + 1 * (c[0] & 0xf800)) / 3) & 0xf800);
                for (var row = 0; row < 4; row++) {
                    var m = src[i + 4 + row];
                    var dstI = (blockY * 4 + row) * width + blockX * 4;
                    dst[dstI++] = c[m & 0x3];
                    dst[dstI++] = c[(m >> 2) & 0x3];
                    dst[dstI++] = c[(m >> 4) & 0x3];
                    dst[dstI++] = c[(m >> 6) & 0x3];
                }
            }
        }
        return dst;
    }
}
//# sourceMappingURL=basis.js.map